import { Dispatch, SetStateAction, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { Grid, Tooltip } from '@mui/material';

import { Badge, CustomAlert, CustomButton, PopupNavigation, Text } from '../../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { BACKGROUND, TEXT } from '../../../../../shared/Helpers/colors';
import { AdminTypes } from '../../../../../components/Menu';

import { UpdateTargetContent } from './Actions/EditTarget';
import GraphsTarget from './Actions/TargetGraph/graphsTarget';
import TargetHistory from './TargetInfo/targetHistory';
import { RowStateProperties } from './tableInfo';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { getTargets, openTarget } from '../../../../../redux/State/clientSlice/programTargetSlice';
import { TargetProperties } from '../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';
import { CustomTableRow, RowTableCell } from '../../../../../shared/uiComponents/Table/tableHelpers';
import Actions from './Actions';
import TargetInfo from './TargetInfo';
import { Info } from '../../../../../shared/Assets/svgIcons';
import { BadgeTypes } from '../../../../../shared/uiComponents/StatusBadge';

export const targetStatusType = {
	Ready: 'gray',
	InTreatment: 'primary',
	Mastered: 'success',
	OnHold: 'warning',
};

export const BTRow = ({ item }: { item: TargetProperties }) => {
	const { targetInstructions, id, programId, name, goalName, sd } = item;
	const cellStyle = {
		p: 2,
		borderBottom: `1px solid ${BACKGROUND.BASE[100]}`,
		borderRight: `1px solid ${BACKGROUND.BASE[100]}`,
	};

	return (
		<div className="marginTop16">
			<Text title={name} className=" marginBottom8" type="h6" font="header" fontWeight="semiBold" />
			<div className="targetRowCard">
				<Grid container>
					<Grid item xs={6} sx={cellStyle}>
						<Text title="Target Name" fontWeight="semiBold" />
						<Text title={name || '-'} color={TEXT.TERTIARY} />
					</Grid>
					<Grid item xs={6} sx={{ ...cellStyle, borderRight: 'none' }}>
						<Text title="Goal" fontWeight="semiBold" />
						<Text title={goalName || '-'} color={TEXT.TERTIARY} />
					</Grid>

					<Grid item xs={6} sx={cellStyle}>
						<Text title="SD" fontWeight="semiBold" />
						<Text title={sd || '-'} color={TEXT.TERTIARY} />
					</Grid>
					<Grid item xs={6} sx={{ ...cellStyle, borderRight: 'none' }}>
						<Text title="Instructions" fontWeight="semiBold" />
						<Text title={targetInstructions || '-'} style={{ whiteSpace: 'pre-line' }} color={TEXT.TERTIARY} />
					</Grid>
				</Grid>
			</div>
			<TargetHistory id={id} programId={programId} />
		</div>
	);
};

const Row = ({
	item,
	rowStates,
	setRowStates,
	allTargets,
	currentIndex,
}: {
	allTargets?: TargetProperties[];
	currentIndex?: number;
	item: TargetProperties;
	rowStates: RowStateProperties[];
	setRowStates: Dispatch<SetStateAction<RowStateProperties[]>>;
}) => {
	const {
		id,
		name,
		sd,
		status,
		responsePercentage,
		dateOpened,
		dateMastered,
		type,
		programId,
		masteryCriteria,
		consecutiveDays,
	} = item;
	const [openSure, setOpenSure] = useState<boolean>(false);
	const [editOpen, setEditOpen] = useState<boolean>(false);
	const [detailsIndex, setDetailsIndex] = useState<number>(currentIndex || 0);

	const includeMastered = useSelector((state) => state.programTarget.includeMastered);
	const loading = useSelector((state) => state.programTarget.loading);
	const role = useSelector((state) => state.account.role);
	const isBT = role?.section.id === AdminTypes.bt;

	const dispatch = useDispatch<DispatchProperties>();

	const openRowActions = rowStates.find((row) => row.id === id)?.open ?? false;

	// For details popup navigation
	const hasDetailsNavigation = allTargets && allTargets.length > 1;
	const currentDetailTarget = hasDetailsNavigation ? allTargets[detailsIndex] : item;

	const openTargetHandler = () => {
		if (!id || !programId) return;

		dispatch(openTarget({ id, programId }))
			.then(unwrapResult)
			.then(() => {
				dispatch(getTargets({ programId, includeMastered }));
				setOpenSure(false);
				toast('Opened');
			})
			.catch((error) => {
				errorNormalizer(error);
				setEditOpen(true);
			});
	};

	const openActionsHandler = () => {
		setDetailsIndex(currentIndex || 0); // Reset to current item when opening
		setRowStates((prev) =>
			prev.map((x) => {
				if (x.id === id) {
					return { ...x, open: !x.open };
				}

				return { ...x, open: false };
			}),
		);
	};

	const handleDetailsPrevious = () => {
		if (detailsIndex > 0) {
			setDetailsIndex(detailsIndex - 1);
		}
	};

	const handleDetailsNext = () => {
		if (allTargets && detailsIndex < allTargets.length - 1) {
			setDetailsIndex(detailsIndex + 1);
		}
	};

	return (
		<>
			<CustomTableRow key={id}>
				<Tooltip arrow title={sd ? sd : 'No SD'} placement="top-start">
					<RowTableCell style={{ wordBreak: 'break-word', wordWrap: 'break-word' }}>
						<Text title={name} />
						<Text title={`SD: ${sd ? sd.substring(0, 60) : 'No SD'}`} fontWeight="medium" />
						<GraphsTarget
							targetId={id}
							targetName={name}
							type={type.id}
							isIcon
							allTargets={allTargets}
							currentIndex={currentIndex}
						/>
					</RowTableCell>
				</Tooltip>
				<RowTableCell>
					<Badge
						label={status.name.split(/(?=[A-Z])/).join(' ')}
						sizeType="sm"
						type={targetStatusType[status.name] as BadgeTypes}
					/>
				</RowTableCell>
				<RowTableCell>
					{responsePercentage ? <Text title={`${responsePercentage}%`} /> : <Text title="-" />}
				</RowTableCell>
				{!isBT && (
					<RowTableCell>
						{dateOpened ? (
							<Text title={dayjs(dateOpened).format('MM/DD/YYYY')} />
						) : openSure ? (
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<CustomButton title="Open" onClick={openTargetHandler} loading={loading} disabled={loading} />
								<CustomButton title="Cancel" buttonType="secondaryGray" onClick={() => setOpenSure(false)} />
							</div>
						) : (
							<CustomButton title="Open Target" onClick={() => setOpenSure(true)} />
						)}
					</RowTableCell>
				)}
				{isBT && (
					<RowTableCell>
						{dateOpened ? <Text title={dayjs(dateOpened).format('MM/DD/YYYY')} /> : <Text title="-" />}
					</RowTableCell>
				)}
				<RowTableCell>
					<Text title={dateMastered ? dayjs(dateMastered).format('MM/DD/YYYY') : '-'} />
				</RowTableCell>
				<RowTableCell>
					<Text title={type.name} />
				</RowTableCell>
				<RowTableCell>{masteryCriteria ? <Text title={masteryCriteria.name} /> : ''}</RowTableCell>
				<RowTableCell>{consecutiveDays ? <Text title={consecutiveDays} /> : <Text title="-" />}</RowTableCell>
				<RowTableCell>
					<Tooltip title="More Information">
						<div onClick={() => openActionsHandler()} className="targetActionWrapper">
							<Info />
						</div>
					</Tooltip>
				</RowTableCell>
			</CustomTableRow>
			<CustomAlert
				open={openRowActions}
				isSecondary
				onClose={() => setRowStates((prev) => prev.map((x) => (x.id === id ? { ...x, open: false } : x)))}
				title="Target Details"
				maxWidth="lg"
				Content={() => {
					if (isBT) return <BTRow item={currentDetailTarget} />;

					return (
						<>
							<Actions item={currentDetailTarget} />
							<TargetInfo
								item={currentDetailTarget}
								id={currentDetailTarget.id}
								programId={currentDetailTarget.programId}
							/>
							{hasDetailsNavigation && (
								<PopupNavigation
									currentIndex={detailsIndex}
									totalCount={allTargets.length}
									onPrevious={handleDetailsPrevious}
									onNext={handleDetailsNext}
								/>
							)}
						</>
					);
				}}
			/>
			<CustomAlert
				open={editOpen}
				onClose={() => setEditOpen(false)}
				title="Please, fill required fields."
				Content={() => (
					<UpdateTargetContent
						targetProperties={item}
						id={id}
						programId={programId}
						closeHandler={() => setEditOpen(false)}
					/>
				)}
			/>
		</>
	);
};

export default Row;
